import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Box,
} from "@material-ui/core";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import ConfirmationModal from "../ConfirmationModal";

// Modal de edicao completa de uma CardTask (checklist do Kanban) -
// reusado tanto no checklist dentro do card quanto na pagina "/tasks".
// Antes so dava pra criar/marcar/excluir, nao tinha como editar titulo,
// responsavel, prazo ou o campo de nota (pedido do usuario 2026-08-29:
// "nao consigo editar", "nao consigo escrever comentarios para
// conclusao, ou pq nao foi concluido").
//
// Fix 2026-10-01: ganhou tambem um modo de CRIACAO (task=null + open),
// pedido do usuario na pagina "/tasks" ("preciso que consiga criar por
// la tambem") - antes so dava pra criar tarefa de dentro de um card
// (BoardCardModal), que e' onde o cardId ja vem pronto. Toda CardTask
// EXIGE um card (FK obrigatoria no banco) - como a pagina "/tasks" nao
// tem "o card atual", o modo de criacao aqui pede pra escolher
// Quadro -> Fase -> Card. Fase entra como filtro intermediario (pedido
// do usuario, boards grandes tipo Comercial tem 50+ cards numa lista
// so' - direto Quadro->Card ficava impossivel de achar o certo).
const TaskEditModal = ({ open, task, onClose, onSaved }) => {
  const isNew = !task;
  const [title, setTitle] = useState("");
  const [assignedUserId, setAssignedUserId] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [note, setNote] = useState("");
  const [done, setDone] = useState(false);
  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [boards, setBoards] = useState([]);
  const [selectedBoardId, setSelectedBoardId] = useState("");
  const [phases, setPhases] = useState([]);
  const [selectedPhaseId, setSelectedPhaseId] = useState("");
  const [newCardTitle, setNewCardTitle] = useState("");
  const [cards, setCards] = useState([]);
  const [selectedCardId, setSelectedCardId] = useState("");
  const [loadingCards, setLoadingCards] = useState(false);

  useEffect(() => {
    if (!open) return;
    api
      .get("/users/")
      .then(({ data }) => setUsers(data.users || []))
      .catch(() => {});
  }, [open]);

  // So busca a lista de quadros quando abre em modo de criacao - no modo
  // de edicao o card ja esta fixo (task.cardId), nunca precisa trocar.
  useEffect(() => {
    if (!open || !isNew) return;
    api
      .get("/boards")
      .then(({ data }) => setBoards(data || []))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isNew]);

  // Fases do quadro escolhido - mesma chamada que BoardMoveTicketModal
  // ja usa pra isso (GET /boards/:id devolve o board com "phases").
  useEffect(() => {
    if (!selectedBoardId) {
      setPhases([]);
      return;
    }
    api
      .get(`/boards/${selectedBoardId}`)
      .then(({ data }) => setPhases(data.phases || []))
      .catch(() => setPhases([]));
  }, [selectedBoardId]);

  useEffect(() => {
    if (!selectedBoardId) {
      setCards([]);
      return;
    }
    setLoadingCards(true);
    api
      .get("/cards", { params: { boardId: selectedBoardId } })
      .then(({ data }) => setCards(data || []))
      .catch(() => setCards([]))
      .finally(() => setLoadingCards(false));
  }, [selectedBoardId]);

  // So mostra os cards DA FASE escolhida - sem isso um board grande
  // (Comercial tem 50+ cards) vira uma lista unica impossivel de achar
  // o card certo. Pedido do usuario 2026-10-01.
  const cardsInPhase = selectedPhaseId
    ? cards.filter((c) => String(c.phaseId) === String(selectedPhaseId))
    : [];

  useEffect(() => {
    if (!open) return;
    if (task) {
      setTitle(task.title || "");
      setAssignedUserId(task.assignedToAll ? "all" : task.assignedUserId || "");
      setDueDate(task.dueDate ? task.dueDate.slice(0, 16) : "");
      setNote(task.note || "");
      setDone(!!task.done);
    } else {
      setTitle("");
      setAssignedUserId("");
      setDueDate("");
      setNote("");
      setDone(false);
      setSelectedBoardId("");
      setSelectedPhaseId("");
      setSelectedCardId("");
      setNewCardTitle("");
    }
  }, [task, open]);

  const NEW_CARD_VALUE = "__new__";

  const handleSave = async () => {
    if (!title.trim()) {
      toast.error("Título é obrigatório.");
      return;
    }
    if (isNew && !selectedCardId) {
      toast.error("Escolha o quadro, a fase e o card dessa tarefa.");
      return;
    }
    if (isNew && selectedCardId === NEW_CARD_VALUE && !newCardTitle.trim()) {
      toast.error("Dê um nome para o novo card.");
      return;
    }
    setSaving(true);
    try {
      if (isNew) {
        let cardId = selectedCardId;
        if (cardId === NEW_CARD_VALUE) {
          const { data: newCard } = await api.post("/cards", {
            boardId: selectedBoardId,
            phaseId: selectedPhaseId,
            title: newCardTitle.trim(),
          });
          cardId = newCard.id;
        }
        await api.post(`/cards/${cardId}/tasks`, {
          title: title.trim(),
          assignedUserId: assignedUserId || null,
          dueDate: dueDate || null,
        });
        toast.success("Tarefa criada!");
      } else {
        await api.put(`/card-tasks/${task.id}`, {
          title: title.trim(),
          assignedUserId: assignedUserId || null,
          dueDate: dueDate || null,
          note,
          done,
        });
        toast.success("Tarefa atualizada!");
      }
      onSaved();
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/card-tasks/${task.id}`);
      toast.success("Tarefa excluída!");
      onSaved();
      onClose();
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isNew ? "Nova tarefa" : "Editar tarefa"}</DialogTitle>
      <DialogContent dividers>
        {isNew && (
          <>
            <TextField
              select
              label="Quadro"
              fullWidth
              margin="dense"
              variant="outlined"
              value={selectedBoardId}
              onChange={(e) => {
                setSelectedBoardId(e.target.value);
                setSelectedPhaseId("");
                setSelectedCardId("");
              }}
            >
              {boards.map((b) => (
                <MenuItem key={b.id} value={b.id}>
                  {b.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Fase"
              fullWidth
              margin="dense"
              variant="outlined"
              value={selectedPhaseId}
              onChange={(e) => {
                setSelectedPhaseId(e.target.value);
                setSelectedCardId("");
              }}
              disabled={!selectedBoardId}
              helperText={!selectedBoardId ? "Escolha o quadro primeiro" : undefined}
            >
              {phases.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Card"
              fullWidth
              margin="dense"
              variant="outlined"
              value={selectedCardId}
              onChange={(e) => setSelectedCardId(e.target.value)}
              disabled={!selectedPhaseId || loadingCards}
              helperText={
                !selectedPhaseId
                  ? "Escolha a fase primeiro"
                  : loadingCards
                  ? "Carregando cards..."
                  : cardsInPhase.length === 0
                  ? "Essa fase não tem nenhum card ainda"
                  : undefined
              }
            >
              {cardsInPhase.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.ticket?.contact?.name || c.title || `Card #${c.id}`}
                </MenuItem>
              ))}
              {selectedPhaseId && (
                <MenuItem value={NEW_CARD_VALUE}>
                  <em>+ Criar novo card nesta fase</em>
                </MenuItem>
              )}
            </TextField>
            {selectedCardId === NEW_CARD_VALUE && (
              <TextField
                label="Nome do novo card"
                fullWidth
                margin="dense"
                variant="outlined"
                value={newCardTitle}
                onChange={(e) => setNewCardTitle(e.target.value)}
                autoFocus
              />
            )}
          </>
        )}
        <TextField
          label="Título"
          fullWidth
          margin="dense"
          variant="outlined"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
        <TextField
          select
          label="Responsável"
          fullWidth
          margin="dense"
          variant="outlined"
          value={assignedUserId}
          onChange={(e) => setAssignedUserId(e.target.value)}
        >
          <MenuItem value="">
            <em>Ninguém</em>
          </MenuItem>
          <MenuItem value="all">
            <em>Todos</em>
          </MenuItem>
          {users.map((u) => (
            <MenuItem key={u.id} value={u.id}>
              {u.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Prazo"
          type="datetime-local"
          fullWidth
          margin="dense"
          variant="outlined"
          InputLabelProps={{ shrink: true }}
          value={dueDate}
          onChange={(e) => setDueDate(e.target.value)}
        />
        {!isNew && (
          <>
            <TextField
              label="Nota (motivo do atraso ou comentário de conclusão)"
              fullWidth
              multiline
              minRows={2}
              maxRows={5}
              margin="dense"
              variant="outlined"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
            <Box mt={1}>
              <FormControlLabel
                control={<Checkbox checked={done} onChange={(e) => setDone(e.target.checked)} />}
                label="Concluída"
              />
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        {!isNew && (
          <Button onClick={() => setConfirmDeleteOpen(true)} style={{ color: "#c62828", marginRight: "auto" }}>
            Excluir tarefa
          </Button>
        )}
        <Button onClick={onClose} color="secondary">
          Cancelar
        </Button>
        <Button onClick={handleSave} color="primary" variant="contained" disabled={saving}>
          Salvar
        </Button>
      </DialogActions>

      <ConfirmationModal
        title="Excluir esta tarefa?"
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        onConfirm={handleDelete}
      >
        Essa ação não pode ser desfeita.
      </ConfirmationModal>
    </Dialog>
  );
};

export default TaskEditModal;
